import React from 'react';
import { Link } from 'react-router-dom';
import EarlyAccessForm from './EarlyAccessForm';
import {
  ArrowRight,
  Building2,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  FileText,
  HardHat,
  Layers,
  Mail,
  MapPinOff,
  MessageSquare,
  Monitor,
  Package,
  Receipt,
  ShieldCheck,
  Smartphone,
  Truck,
  WifiOff,
} from 'lucide-react';

const CONTACT_EMAIL = 'info@simplicitydynamics.ca';

// Blueprint-grid background used on the dark sections (mobile app theme).
const blueprintGrid = {
  backgroundImage:
    'linear-gradient(rgba(158,203,247,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(158,203,247,0.06) 1px, transparent 1px)',
  backgroundSize: '32px 32px',
};

const Monogram = ({ className = '' }) => (
  <span
    className={`inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-[#9ecbf7]/40 bg-[#9ecbf7]/10 text-sm font-bold tracking-wide text-[#9ecbf7] ${className}`}
  >
    SD
  </span>
);

const NAV_LINKS = [
  { label: 'Product', href: '#product' },
  { label: 'Features', href: '#features' },
  { label: 'Field app', href: '#field' },
  { label: 'Contact', href: '#contact' },
];

/* ---------- decorative app mockups (CSS only) ---------- */

const BrowserMockup = () => (
  <div className="overflow-hidden rounded-xl border border-[#9ecbf7]/20 bg-[#151f2c] shadow-2xl">
    <div className="flex items-center gap-1.5 border-b border-white/5 bg-[#0c131d] px-4 py-2.5">
      <span className="h-2.5 w-2.5 rounded-full bg-[#d96a5b]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#e0a23c]" />
      <span className="h-2.5 w-2.5 rounded-full bg-[#2ea183]" />
      <span className="mx-auto rounded bg-white/5 px-3 py-0.5 text-[10px] text-[#93a4b3]">
        simplicity-office.web.app
      </span>
    </div>
    <div className="flex">
      <div className="flex w-10 flex-col gap-2 bg-[#101a26] p-2 pt-3">
        <span className="h-1 w-6 rounded bg-[#9ecbf7]/70" />
        <span className="h-1 w-6 rounded bg-[#9ecbf7]/25" />
        <span className="h-1 w-6 rounded bg-[#9ecbf7]/25" />
        <span className="h-1 w-6 rounded bg-[#9ecbf7]/25" />
        <span className="h-1 w-6 rounded bg-[#9ecbf7]/25" />
      </div>
      <div className="flex-1 space-y-2 bg-[#eef1f5] p-3">
        <div className="grid grid-cols-4 gap-2 rounded-lg bg-gradient-to-r from-[#4a5c70] to-[#2a3a4c] p-2.5">
          {[
            { n: '6', label: 'Jobsites' },
            { n: '18', label: 'On site' },
            { n: '4', label: 'Vehicles' },
            { n: '2', label: 'Attention', warn: true },
          ].map(({ n, label, warn }) => (
            <div key={label} className="rounded bg-white/10 p-1.5 text-center">
              <div className={`text-xs font-bold ${warn ? 'text-[#f3c473]' : 'text-white'}`}>{n}</div>
              <div className="text-[7px] uppercase tracking-wide text-[#c9d6e2]">{label}</div>
            </div>
          ))}
        </div>
        {[
          ['#2ea183', '#2f66b8'],
          ['#e0a23c', '#2ea183'],
        ].map((dots, i) => (
          <div key={i} className="rounded-lg bg-white p-2 shadow-sm">
            <div className="mb-1.5 h-1.5 w-16 rounded bg-[#64798a]/30" />
            {dots.map((c) => (
              <div key={c} className="mb-1 flex items-center gap-1.5 last:mb-0">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: c }} />
                <span className="h-1.5 flex-1 rounded bg-[#c6cdd6]/60" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const PhoneMockup = () => (
  <div className="overflow-hidden rounded-[1.8rem] border-[6px] border-[#0a1017] bg-[#0f1620] shadow-2xl">
    <div className="mx-auto mt-1.5 h-1 w-12 rounded-full bg-white/15" />
    <div className="space-y-2 p-2.5 pb-4" style={blueprintGrid}>
      <div className="flex items-center justify-between px-0.5">
        <span className="text-[10px] font-bold tracking-wide text-[#9ecbf7]">SimpleWork</span>
        <span className="h-1.5 w-1.5 rounded-full bg-[#2ea183]" />
      </div>
      <div className="rounded-xl bg-[#2ea183] py-2.5 text-center">
        <div className="text-[10px] font-bold tracking-widest text-white">CLOCK IN</div>
        <div className="text-[7px] text-white/80">Riverside Towers</div>
      </div>
      {[
        { Icon: ClipboardCheck, label: 'Daily checklist', c: '#2ea183' },
        { Icon: ShieldCheck, label: 'Hazard assessment', c: '#9ecbf7' },
        { Icon: Layers, label: 'Blueprints', c: '#e0a23c' },
      ].map(({ Icon, label, c }) => (
        <div key={label} className="flex items-center gap-1.5 rounded-lg bg-white/5 p-1.5">
          <Icon size={10} color={c} />
          <span className="text-[8px] text-[#dfe7ee]">{label}</span>
        </div>
      ))}
      <div className="relative h-12 overflow-hidden rounded-lg bg-[#101b29]" style={blueprintGrid}>
        <span className="absolute left-2 top-2 h-6 w-8 -rotate-3 rounded-sm border border-[#2ea183]/80" />
        <span className="absolute right-2 top-3 h-5 w-7 rotate-2 rounded-sm border border-[#e0a23c]/80" />
      </div>
    </div>
  </div>
);

/* ---------- page ---------- */

const CompanyFrontPage = () => {
  return (
    <div className="min-h-screen bg-[#f2f5f8] text-[#1d2b33]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-[#0f1620]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <a href="#top" className="flex items-center gap-3">
            <Monogram />
            <span className="text-lg font-semibold text-white">Simplicity Dynamics</span>
          </a>
          <nav className="hidden items-center gap-6 md:flex">
            {NAV_LINKS.map(({ label, href }) => (
              <a
                key={href}
                href={href}
                className="text-sm font-medium text-[#c9d6e2] transition-colors hover:text-[#9ecbf7]"
              >
                {label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative overflow-hidden bg-[#0f1620]" style={blueprintGrid}>
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(60% 50% at 30% 20%, rgba(158,203,247,0.10) 0%, transparent 70%)',
          }}
        />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#2ea183]/40 bg-[#2ea183]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#5cd6b2]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#2ea183]" />
              Launching soon — early access open
            </span>
            <h1 className="mt-5 text-4xl font-bold leading-tight text-white [text-wrap:balance] md:text-5xl">
              Run your whole construction company from one place.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-[#b7c5d1]">
              One web app for the office. One simple mobile app for the crews. Time tracking with
              GPS, scheduling, safety paperwork, blueprints with live progress, materials and
              estimates — without the complexity.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 rounded-lg bg-[#9ecbf7] px-6 py-3 font-semibold text-[#0f1620] transition-colors hover:bg-[#b8daf9]"
              >
                <Mail size={18} />
                Request early access
              </a>
              <a
                href="#features"
                className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-6 py-3 font-medium text-[#dfe7ee] transition-colors hover:bg-white/5"
              >
                See the features
                <ArrowRight size={18} />
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#93a4b3]">
              {['Works offline', 'Built for any trade', 'Made in Calgary'].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 size={15} className="text-[#2ea183]" />
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div aria-hidden="true" className="relative mx-auto w-full max-w-md pb-14 pr-8 sm:pr-14">
            <BrowserMockup />
            <div className="absolute -bottom-2 -right-1 w-36 sm:w-40">
              <PhoneMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Two apps */}
      <section id="product" className="scroll-mt-24 py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="text-center text-3xl font-bold [text-wrap:balance]">Two apps. One system.</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-[#64798a]">
            SimpleOffice sees everything live. SimpleWork needs three taps. Both always agree.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-[#96a5b4]/25 bg-white p-7 shadow-lg">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#dcebfb] text-[#2f66b8]">
                  <Monitor size={20} />
                </span>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#64798a]">
                    SimpleOffice · web app
                  </div>
                  <h3 className="text-lg font-semibold">Everything on one dashboard</h3>
                </div>
              </div>
              <ul className="mt-5 space-y-2.5 text-[15px] text-[#3c4f5e]">
                {[
                  'Live view of jobsites, who is clocked in, and what needs attention',
                  'Timesheets and payroll-ready exports',
                  'Crew scheduling and time-off requests',
                  'Safety records organized and ready for audits',
                  'Estimates, purchase orders and expenses in one place',
                ].map((t) => (
                  <li key={t} className="flex gap-2.5">
                    <CheckCircle2 size={18} className="mt-0.5 flex-none text-[#2ea183]" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-[#96a5b4]/25 bg-white p-7 shadow-lg">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#d9f2ea] text-[#1f8a6d]">
                  <Smartphone size={20} />
                </span>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-[#64798a]">
                    SimpleWork · mobile app
                  </div>
                  <h3 className="text-lg font-semibold">Made for the crew&rsquo;s pocket</h3>
                </div>
              </div>
              <ul className="mt-5 space-y-2.5 text-[15px] text-[#3c4f5e]">
                {[
                  'One-tap clock in and out, with GPS on the jobsite',
                  'Daily equipment and vehicle checks',
                  'Hazard assessments and toolbox talks, signed on the phone',
                  'Blueprints with markup — see what is done, mark your work',
                  'Works offline and syncs when the signal comes back',
                ].map((t) => (
                  <li key={t} className="flex gap-2.5">
                    <CheckCircle2 size={18} className="mt-0.5 flex-none text-[#2ea183]" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="scroll-mt-24 bg-[#e8edf2] py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="text-center text-3xl font-bold [text-wrap:balance]">What&rsquo;s inside</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-[#64798a]">
            Modules that talk to each other, so nothing gets typed twice.
          </p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {[
              { Icon: Clock, title: 'Time & GPS', desc: 'Geofenced clock-in, breaks and overtime — payroll-ready.' },
              { Icon: Truck, title: 'Fleet tracking', desc: 'Trucks and equipment on a live map; trips, after-hours alerts and maintenance reminders.' },
              { Icon: CalendarDays, title: 'Scheduling', desc: 'Assign crews to jobsites; time off handled in the same place.' },
              { Icon: ShieldCheck, title: 'Safety & compliance', desc: 'Hazards, toolbox talks, incidents and inspections — audit-ready.' },
              { Icon: Layers, title: 'Blueprints & progress', desc: 'Plans on every phone; mark work and see live percent complete.' },
              { Icon: Package, title: 'Materials & orders', desc: 'Estimate materials from blueprints; send purchase orders.' },
              { Icon: FileText, title: 'Quotes', desc: 'Build quotes fast; clients answer right from the email.' },
              { Icon: Receipt, title: 'Expenses & costs', desc: 'Snap receipts; see labor and spending per jobsite.' },
              { Icon: MessageSquare, title: 'Chat & reports', desc: 'Team chat, site photos and clean PDF reports.' },
              { Icon: Building2, title: 'Simple GC', desc: 'Site access, daily muster and punch lists for general contractors.', soon: true },
            ].map(({ Icon, title, desc, soon }) => (
              <div key={title} className="rounded-xl border border-[#96a5b4]/25 bg-white p-5 shadow-lg">
                <div className="flex items-start justify-between gap-2">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#dcebfb] text-[#2f66b8]">
                    <Icon size={20} />
                  </span>
                  {soon && (
                    <span className="rounded-full border border-[#e0a23c]/50 bg-[#e0a23c]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#b47d1f]">
                      Coming
                    </span>
                  )}
                </div>
                <h3 className="mt-3 font-semibold">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-[#64798a]">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Field band */}
      <section id="field" className="scroll-mt-24 bg-[#0f1620]" style={blueprintGrid}>
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-white [text-wrap:balance]">Built for real job sites</h2>
            <p className="mt-4 text-lg leading-relaxed text-[#b7c5d1]">
              No signal in the basement? Everything keeps working and syncs later. Big buttons and
              short flows — screens made for gloves, not office chairs. And workers are located only
              while they are clocked in — never on their own time.
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { Icon: WifiOff, t: 'Offline-first' },
              { Icon: HardHat, t: 'Field-first design' },
              { Icon: ClipboardCheck, t: 'Paper, replaced' },
              { Icon: MapPinOff, t: 'Location only on the clock' },
            ].map(({ Icon, t }) => (
              <div
                key={t}
                className="flex items-center justify-center gap-2.5 rounded-xl border border-white/10 bg-white/5 px-4 py-4 text-[#dfe7ee]"
              >
                <Icon size={20} className="text-[#9ecbf7]" />
                <span className="font-medium">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screens */}
      <section className="bg-[#e8edf2] py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="text-center text-3xl font-bold [text-wrap:balance]">A look at SimpleWork</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-[#64798a]">
            Home, Work and Safety. Three tabs, one tap to anything.
          </p>
          <div className="mx-auto mt-10 grid max-w-3xl gap-8 sm:grid-cols-3">
            {[
              { src: '/screens/simplework-home.png', alt: 'SimpleWork home screen with the clock-in card and today’s items' },
              { src: '/screens/simplework-work.png', alt: 'SimpleWork work tab with blueprints, progress, expenses and checklists' },
              { src: '/screens/simplework-safety.png', alt: 'SimpleWork safety tab with hazard, meeting and inspection tools' },
            ].map(({ src, alt }) => (
              <div
                key={src}
                className="mx-auto w-full max-w-[240px] overflow-hidden rounded-[1.6rem] border-[6px] border-[#1a2733] bg-[#0f1620] shadow-xl"
              >
                <img src={src} alt={alt} loading="lazy" width={540} height={1085} className="block h-auto w-full" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About + contact */}
      <section id="contact" className="scroll-mt-24 py-16 lg:py-20">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-2">
          <div>
            <h2 className="text-3xl font-bold [text-wrap:balance]">About us</h2>
            <p className="mt-4 leading-relaxed text-[#3c4f5e]">
              Simplicity Dynamics is a software company from Calgary, Alberta. The product was born
              on real job sites — built by people who got tired of paper timesheets, lost safety
              forms, and five different apps that don&rsquo;t talk to each other.
            </p>
            <p className="mt-3 leading-relaxed text-[#3c4f5e]">
              We are finishing the last pieces now and bringing our first companies on board.
            </p>
          </div>
          <div className="rounded-xl border border-[#96a5b4]/25 bg-white p-7 shadow-lg">
            <h3 className="text-xl font-semibold">Be one of the first on board</h3>
            <p className="mt-2 text-[#64798a]">
              Tell us your trade and crew size — we&rsquo;ll get back to you with early access.
            </p>
            <div className="relative mt-5">
              <EarlyAccessForm />
            </div>
            <p className="mt-4 text-sm text-[#64798a]">
              Prefer email? Write to{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-[#2f66b8] hover:underline">
                {CONTACT_EMAIL}
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#0f1620] py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 sm:px-6 md:flex-row">
          <div className="flex items-center gap-3">
            <Monogram className="h-8 w-8 text-xs" />
            <span className="text-sm text-[#93a4b3]">
              &copy; 2026 Simplicity Dynamics Inc. &middot; Calgary, Alberta, Canada
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="text-[#93a4b3] transition-colors hover:text-[#9ecbf7]">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-[#93a4b3] transition-colors hover:text-[#9ecbf7]">
              Terms &amp; Conditions
            </Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#93a4b3] transition-colors hover:text-[#9ecbf7]">
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CompanyFrontPage;
